import type {
  ApiChain,
  ApiCheckTransactionDraftResult,
  ApiSubmitTransferOptions,
  ApiTransferPayload,
} from '../../../api/types';
import type { TransferFeeDraft } from '../../reducers';
import type { GlobalState } from '../../types';
import { ApiCommonError } from '../../../api/types';
import { ApiTransactionDraftError } from '../../../api/types';
import { ScamWarningType, TransferState } from '../../types';

import { DEFAULT_CHAIN, IS_LEGENDS_WALLET, NFT_BATCH_SIZE } from '../../../config';
import { bigintDivideToNumber } from '../../../util/bigint';
import { getDoesUsePinPad } from '../../../util/biometrics';
import { getChainConfig } from '../../../util/chain';
import { toDecimal } from '../../../util/decimals';
import { getDieselTokenAmount } from '../../../util/fee/transferFee';
import { split } from '../../../util/iteratees';
import { getTranslation } from '../../../util/langProvider';
import { shouldShowDomainScamWarning, shouldShowSeedPhraseScamWarning } from '../../../util/scamDetection';
import { pause } from '../../../util/schedulers';
import { getTransferQuoteRefreshDelay } from '../../../util/transferQuote';
import { callApi, callApiWithThrow } from '../../../api';
import { withEnclaveSessionRelease } from '../../helpers/enclave';
import { handleTransferResult, isErrorTransferResult, prepareTransfer } from '../../helpers/transfer';
import { addActionHandler, getGlobal, setGlobal } from '../../index';
import {
  clearCurrentTransfer,
  clearIsPinAccepted,
  isSameTransferFeeDraft,
  preserveMaxTransferAmount,
  updateAccountState,
  updateCurrentTransfer,
  updateCurrentTransferByCheckResult,
  updateCurrentTransferLoading,
} from '../../reducers';
import { removeActivities, updateActivity } from '../../reducers/activities';
import {
  selectAccountState,
  selectCurrentAccount,
  selectCurrentAccountId,
  selectCurrentAccountTokens,
  selectCurrentNetwork,
  selectEnclaveToken,
  selectIsHardwareAccount,
  selectToken,
} from '../../selectors';
import { switchAccount } from './auth';

let latestTransferFeeRequestId = 0;

const PREFETCH_REUSE_MS = 30_000;

type PrefetchedTransferQuote = {
  accountId: string;
  draftRevision?: number;
  draft: TransferFeeDraft;
  startedAt: number;
  completedAt?: number;
  result?: ApiCheckTransactionDraftResult;
  promise: Promise<ApiCheckTransactionDraftResult | undefined>;
};

let prefetchedTransferQuote: PrefetchedTransferQuote | undefined;
let inFlightPrefetch: PrefetchedTransferQuote | undefined;
let queuedPrefetch: {
  accountId: string; draftRevision?: number; draft: TransferFeeDraft & { amount: bigint };
} | undefined;

export function resetPrefetchedTransferQuoteForTests() {
  prefetchedTransferQuote = undefined;
  inFlightPrefetch = undefined;
  queuedPrefetch = undefined;
}

function isPrefetchedQuoteCurrent(
  entry: PrefetchedTransferQuote | undefined,
  global: GlobalState,
  accountId: string,
  draft: TransferFeeDraft,
) {
  if (!entry || entry.accountId !== accountId || entry.draftRevision !== global.currentTransfer.draftRevision
    || !isSameTransferFeeDraft(global.currentTransfer, draft)) return false;
  const cachedDraft = {
    ...global.currentTransfer,
    ...entry.draft,
    comment: entry.draft.comment,
    shouldEncrypt: entry.draft.shouldEncrypt,
    binPayload: entry.draft.binPayload,
    stateInit: entry.draft.stateInit,
    nfts: undefined,
  };
  if (!isSameTransferFeeDraft(cachedDraft, draft)) return false;

  const now = Date.now();
  if (now - (entry.completedAt ?? entry.startedAt) >= PREFETCH_REUSE_MS) return false;
  const expiresAt = entry.result?.sponsorship?.expiresAt;
  return !expiresAt || getTransferQuoteRefreshDelay(expiresAt) > 0;
}

function requestTransferDraftQuote(
  global: GlobalState, accountId: string, draft: TransferFeeDraft, allowRepeat = false,
) {
  const { tokenAddress, chain } = selectToken(global, draft.tokenSlug);
  return callApi('checkTransactionDraft', chain, {
    accountId,
    tokenAddress,
    toAddress: draft.toAddress,
    amount: draft.amount,
    payload: getTransferPayload(chain, draft.binPayload, draft.comment, draft.shouldEncrypt),
    stateInit: draft.stateInit,
    allowGasless: true,
    allowRepeat,
  });
}

addActionHandler('prefetchTransferQuote', (global, actions, draft) => {
  if (!IS_LEGENDS_WALLET || global.currentTransfer.state !== TransferState.Initial
    || global.currentTransfer.isOfframp || global.currentTransfer.nfts?.length
    || !draft.amount || !isSameTransferFeeDraft(global.currentTransfer, draft)) return;

  const accountId = selectCurrentAccountId(global);
  if (!accountId || isPrefetchedQuoteCurrent(prefetchedTransferQuote, global, accountId, draft)) return;

  if (inFlightPrefetch) {
    queuedPrefetch = {
      accountId, draftRevision: global.currentTransfer.draftRevision, draft: { ...draft, amount: draft.amount },
    };
    prefetchedTransferQuote = undefined;
    return;
  }

  const entry: PrefetchedTransferQuote = {
    accountId,
    draftRevision: global.currentTransfer.draftRevision,
    draft,
    startedAt: Date.now(),
    promise: Promise.resolve(undefined),
  };
  prefetchedTransferQuote = entry;
  inFlightPrefetch = entry;
  entry.promise = requestTransferDraftQuote(global, accountId, draft).then(
    (result) => result,
    () => undefined,
  ).then((result) => {
    if (prefetchedTransferQuote === entry) {
      if (!result || 'error' in result) {
        prefetchedTransferQuote = undefined;
      } else {
        entry.result = result;
        entry.completedAt = Date.now();
      }
    }
    if (inFlightPrefetch === entry) {
      inFlightPrefetch = undefined;
      const queued = queuedPrefetch;
      queuedPrefetch = undefined;
      const current = getGlobal();
      if (queued && current.currentTransfer.state === TransferState.Initial
        && selectCurrentAccountId(current) === queued.accountId
        && current.currentTransfer.draftRevision === queued.draftRevision
        && isSameTransferFeeDraft(current.currentTransfer, queued.draft)) {
        actions.prefetchTransferQuote(queued.draft);
      }
    }
    return result;
  });
});

function requireTransferFeeAuthorization(global: GlobalState) {
  return updateCurrentTransfer(global, {
    state: TransferState.Password,
    isLoading: false,
    error: undefined,
    explainedFee: undefined,
    sponsorship: undefined,
    isFeeBalanceAuthorizationRequired: true,
  });
}

addActionHandler('switchTransferAccount', async (global, actions, { accountId }) => {
  if (accountId === selectCurrentAccountId(global)) {
    return;
  }
  if (global.currentTransfer.hasFeeBalanceAuthorizationSession) {
    const enclaveToken = selectEnclaveToken(global);
    if (enclaveToken) actions.releaseEnclaveSession({ enclaveToken });
  }

  // `switchAccount` clears `currentTransfer`, so snapshot the user-entered draft and re-apply it afterwards
  const {
    toAddress,
    resolvedAddress,
    toAddressName,
    amount,
    comment,
    shouldEncrypt,
    tokenSlug,
    binPayload,
    stateInit,
    isMemoRequired,
  } = global.currentTransfer;

  await switchAccount(global, accountId);

  global = getGlobal();
  setGlobal(updateCurrentTransfer(global, {
    state: TransferState.Initial,
    toAddress,
    resolvedAddress,
    toAddressName,
    amount,
    comment,
    shouldEncrypt,
    tokenSlug,
    binPayload,
    stateInit,
    isMemoRequired,
  }));
});

addActionHandler('submitTransferInitial', async (global, actions, payload) => {
  const {
    tokenSlug,
    toAddress,
    amount,
    comment,
    shouldEncrypt,
    nfts,
    isGasless,
    isGaslessWithStars,
    isNftBurn,
    allowRepeat: isRepeatExplicitlyAllowed,
  } = payload;

  const currentAccountId = selectCurrentAccountId(global)!;
  const repeatAuthorization = global.currentTransfer.repeatAuthorization;
  const allowRepeat = isRepeatExplicitlyAllowed === true || (repeatAuthorization?.accountId === currentAccountId
    && repeatAuthorization.tokenSlug === tokenSlug
    && repeatAuthorization.toAddress === toAddress
    && repeatAuthorization.amount === amount);
  const isNftTransfer = Boolean(nfts?.length);
  const isEarlyConfirm = IS_LEGENDS_WALLET && !isNftTransfer && !global.currentTransfer.isOfframp;
  const draftRevision = global.currentTransfer.draftRevision;
  const requestId = isEarlyConfirm ? ++latestTransferFeeRequestId : undefined;

  setGlobal(updateCurrentTransfer(global, isEarlyConfirm ? {
    state: TransferState.Confirm,
    isLoading: true,
    isQuoteReady: false,
    error: undefined,
    explainedFee: undefined,
    sponsorship: undefined,
    toAddress,
    resolvedAddress: toAddress,
    amount,
    comment,
    shouldEncrypt,
    tokenSlug,
    isGasless,
    isGaslessWithStars,
    isNftBurn,
  } : { isLoading: true }));

  let result: ApiCheckTransactionDraftResult | undefined;

  try {
    if (isNftTransfer) {
      const chain = nfts?.[0]?.chain || DEFAULT_CHAIN;

      result = await callApi('checkNftTransferDraft', chain, {
        accountId: currentAccountId,
        nfts,
        toAddress,
        comment,
        isNftBurn,
      });
    } else {
      const prefetched = !allowRepeat && isEarlyConfirm
        && isPrefetchedQuoteCurrent(prefetchedTransferQuote, global, currentAccountId, payload)
        ? prefetchedTransferQuote : undefined;
      result = prefetched ? await prefetched.promise : undefined;
      if (!result || 'error' in result || !isPrefetchedQuoteCurrent(prefetched, global, currentAccountId, payload)) {
        result = await requestTransferDraftQuote(global, currentAccountId, payload, allowRepeat);
      }
    }
  } catch {
    result = { error: ApiCommonError.ServerError };
  }

  global = getGlobal();
  if (isEarlyConfirm && (requestId !== latestTransferFeeRequestId
    || selectCurrentAccountId(global) !== currentAccountId
    || global.currentTransfer.state !== TransferState.Confirm
    || global.currentTransfer.draftRevision !== draftRevision
    || !isSameTransferFeeDraft(global.currentTransfer, payload))) return;
  global = updateCurrentTransferLoading(global, false);

  if (result?.error === ApiTransactionDraftError.WalletPrepaidAuthorizationRequired) {
    setGlobal(requireTransferFeeAuthorization(global));
    return;
  }

  if (result?.error === ApiTransactionDraftError.WalletSponsorshipAlreadySent) {
    setGlobal(updateCurrentTransfer(global, {
      ...(isEarlyConfirm ? { state: TransferState.Initial } : {}),
      repeatRequired: true,
      repeatAuthorization: undefined,
    }));
    return;
  }

  if (isEarlyConfirm && result?.sponsorship?.isPrepaidInsufficient) {
    prefetchedTransferQuote = undefined;
    setGlobal(updateCurrentTransfer(global, {
      state: TransferState.Initial,
      isQuoteReady: false,
      sponsorship: result.sponsorship,
    }));
    return;
  }

  if (result) {
    global = updateCurrentTransferByCheckResult(global, result);
  }

  if (isEarlyConfirm && result?.error === ApiTransactionDraftError.InsufficientBalance
    && global.currentTransfer.amount !== amount && global.currentTransfer.amount !== undefined
    && global.currentTransfer.amount > 0n) {
    const adjustedAmount = global.currentTransfer.amount;
    setGlobal(updateCurrentTransfer(global, {
      state: TransferState.Initial,
      isLoading: false,
      draftRevision: (draftRevision ?? 0) + 1,
    }));
    actions.submitTransferInitial({ ...payload, amount: adjustedAmount });
    return;
  }

  if (!result || 'error' in result) {
    setGlobal(isEarlyConfirm ? updateCurrentTransfer(global, {
      state: TransferState.Initial,
      isQuoteReady: false,
    }) : global);

    actions.showError({ error: result?.error ?? ApiCommonError.ServerError });

    return;
  }

  setGlobal(updateCurrentTransfer(global, {
    state: TransferState.Confirm,
    isQuoteReady: true,
    error: undefined,
    repeatRequired: false,
    repeatAuthorization: allowRepeat && result.sponsorship ? {
      accountId: currentAccountId,
      tokenSlug,
      toAddress,
      amount,
    } : global.currentTransfer.repeatAuthorization,
    toAddress,
    resolvedAddress: result.resolvedAddress,
    amount,
    comment,
    shouldEncrypt,
    tokenSlug,
    isToNewAddress: result.isToAddressNew,
    isGasless,
    isGaslessWithStars,
    isNftBurn,
    isFeeBalanceAuthorizationRequired: false,
  }));
});

addActionHandler('fetchTransferFee', async (global, actions, payload) => {
  const requestId = ++latestTransferFeeRequestId;
  const draftRevision = global.currentTransfer.draftRevision;
  global = updateCurrentTransfer(global, {
    isLoading: true,
    error: undefined,
    explainedFee: undefined,
    sponsorship: undefined,
    repeatRequired: false,
    repeatAuthorization: undefined,
  });
  setGlobal(global);

  const {
    tokenSlug, toAddress, amount, comment, shouldEncrypt, binPayload, stateInit,
  } = payload;

  const { tokenAddress, chain } = selectToken(global, tokenSlug);
  const accountId = selectCurrentAccountId(global)!;

  const result = await callApi('checkTransactionDraft', chain, {
    accountId,
    toAddress,
    amount,
    payload: getTransferPayload(chain, binPayload, comment, shouldEncrypt),
    tokenAddress,
    stateInit,
    allowGasless: true,
    allowRepeat: payload.allowRepeat === true,
  });

  global = getGlobal();
  if (requestId !== latestTransferFeeRequestId) {
    return;
  }
  if (selectCurrentAccountId(global) !== accountId
    || global.currentTransfer.draftRevision !== draftRevision
    || !isSameTransferFeeDraft(global.currentTransfer, payload)) {
    setGlobal(updateCurrentTransfer(global, { isLoading: false }));
    return;
  }

  if (result?.error === ApiTransactionDraftError.WalletPrepaidAuthorizationRequired) {
    setGlobal(requireTransferFeeAuthorization(global));
    return;
  }

  if (result?.error === ApiTransactionDraftError.WalletSponsorshipAlreadySent) {
    setGlobal(updateCurrentTransfer(global, {
      isLoading: false,
      repeatRequired: true,
      repeatAuthorization: undefined,
    }));
    return;
  }

  global = updateCurrentTransfer(global, {
    isLoading: false,
    repeatRequired: false,
    repeatAuthorization: result && !result.error && result.sponsorship && payload.allowRepeat ? {
      accountId,
      tokenSlug,
      toAddress,
      amount,
    } : undefined,
  });
  if (result) {
    global = updateCurrentTransferByCheckResult(global, result);
    if (global.currentTransfer.amount !== amount) {
      global = updateCurrentTransfer(global, { repeatAuthorization: undefined });
    }
  } else {
    global = updateCurrentTransfer(global, {
      explainedFee: undefined,
      sponsorship: undefined,
    });
  }
  setGlobal(global);

  if (result?.error && result.error !== ApiTransactionDraftError.InsufficientBalance) {
    actions.showError({ error: result.error });
  }

  if (!IS_LEGENDS_WALLET && result?.error === ApiTransactionDraftError.InsufficientBalance) {
    const currentAccount = selectCurrentAccount(global)!;
    const accountTokens = selectCurrentAccountTokens(global)!;
    const { chain } = selectToken(global, tokenSlug);

    if (shouldShowSeedPhraseScamWarning(currentAccount, accountTokens, chain)) {
      global = getGlobal();
      global = updateCurrentTransfer(global, { scamWarningType: ScamWarningType.SeedPhrase });
      setGlobal(global);
    }
  }

  if (result?.error !== ApiCommonError.DomainNotResolved && shouldShowDomainScamWarning(toAddress)) {
    global = getGlobal();
    global = updateCurrentTransfer(global, { scamWarningType: ScamWarningType.DomainLike });
    setGlobal(global);
  }
});

addActionHandler('authorizeTransferFeeAccess', async (global, actions, payload) => {
  const accountId = selectCurrentAccountId(global)!;
  setGlobal(updateCurrentTransfer(global, { isLoading: true, error: undefined }));

  try {
    await callApiWithThrow('ensureWalletPrepaidAccess', accountId, payload.enclaveToken);
  } catch {
    actions.releaseEnclaveSession({ enclaveToken: payload.enclaveToken });
    global = getGlobal();
    setGlobal(updateCurrentTransfer(global, selectCurrentAccountId(global) === accountId ? {
      isLoading: false,
      error: ApiCommonError.ServerError,
    } : { isLoading: false }));
    return;
  }

  global = getGlobal();
  if (selectCurrentAccountId(global) !== accountId) {
    actions.releaseEnclaveSession({ enclaveToken: payload.enclaveToken });
    setGlobal(updateCurrentTransfer(global, { isLoading: false }));
    return;
  }

  const {
    tokenSlug, toAddress, amount, comment, shouldEncrypt, binPayload, stateInit,
  } = global.currentTransfer;
  setGlobal(updateCurrentTransfer(global, {
    state: TransferState.Initial,
    isLoading: false,
    error: undefined,
    isFeeBalanceAuthorizationRequired: false,
    hasFeeBalanceAuthorizationSession: true,
  }));

  if (toAddress && amount) {
    const quotePayload = {
      tokenSlug,
      toAddress,
      amount,
      comment,
      shouldEncrypt,
      binPayload,
      stateInit,
    };
    if (IS_LEGENDS_WALLET) {
      actions.submitTransferInitial(quotePayload);
    } else {
      actions.fetchTransferFee(quotePayload);
    }
  }
});

addActionHandler('fetchNftFee', async (global, actions, payload) => {
  const requestId = ++latestTransferFeeRequestId;
  const { toAddress, nfts, comment } = payload;

  global = updateCurrentTransfer(global, { isLoading: true, error: undefined });
  setGlobal(global);

  const chain = nfts?.[0]?.chain || DEFAULT_CHAIN;

  const result = await callApi('checkNftTransferDraft', chain, {
    accountId: selectCurrentAccountId(global)!,
    nfts,
    toAddress,
    comment: getNftTransferComment(chain, comment),
  });

  global = getGlobal();

  if (requestId !== latestTransferFeeRequestId) {
    return;
  }
  if (!global.currentTransfer.nfts?.length) {
    // For cases when the user switches the token transfer mode before the result arrives
    setGlobal(updateCurrentTransfer(global, { isLoading: false }));
    return;
  }

  global = updateCurrentTransfer(global, { isLoading: false });
  if (result) {
    global = updateCurrentTransferByCheckResult(global, result);
  }
  if (shouldShowDomainScamWarning(toAddress)) {
    global = updateCurrentTransfer(global, { scamWarningType: ScamWarningType.DomainLike });
  }
  setGlobal(global);

  const nativeToken = getChainConfig(chain).nativeToken;

  if (result?.error) {
    const feeDisplay = result.explainedFee?.fullFee?.nativeSum;

    actions.showError({
      error: result?.error === ApiTransactionDraftError.InsufficientBalance
        ? getTranslation('Insufficient %token% for fee.%fee%', {
          token: nativeToken.slug,
          fee: feeDisplay !== undefined
            ? ` (~${toDecimal(feeDisplay, nativeToken.decimals)} ${nativeToken.slug})`
            : '',
        })
        : result.error,
    });
  }
});

addActionHandler('submitTransfer', withEnclaveSessionRelease(async (global, actions, payload) => {
  const confirmationState = global.currentTransfer.state;
  const enclaveToken = payload?.enclaveToken;
  const {
    resolvedAddress,
    comment,
    amount,
    promiseId,
    tokenSlug,
    shouldEncrypt,
    binPayload,
    nfts,
    isGasless,
    diesel,
    stateInit,
    isGaslessWithStars,
    isNftBurn,
    sponsorship,
  } = global.currentTransfer;

  if (!prepareTransfer(TransferState.ConfirmHardware, updateCurrentTransfer)) {
    return;
  }

  if (confirmationState === TransferState.Password && !promiseId) {
    setGlobal(updateCurrentTransfer(getGlobal(), { state: TransferState.Submitting }));
  }

  // This is a part of the legacy dapp transaction mechanism. See `promiseId` in `src/global/types.ts` for more details.
  if (promiseId) {
    await callApi('confirmDappRequest', promiseId, enclaveToken);
    return;
  }

  global = getGlobal();

  const currentAccountId = selectCurrentAccountId(global)!;

  const { explainedFee } = global.currentTransfer;
  const fullNativeFee = explainedFee?.fullFee?.nativeSum;
  const realNativeFee = explainedFee?.realFee?.nativeSum;

  let result: { activityId?: string; mfaRequestHash?: string }
    | { activityIds: string[] } | { error: string } | undefined;

  if (nfts?.length) {
    const chain = nfts?.[0]?.chain || DEFAULT_CHAIN;
    const chunks = split(nfts, selectIsHardwareAccount(global) ? 1 : NFT_BATCH_SIZE);

    for (const chunk of chunks) {
      const batchResult = await callApi(
        'submitNftTransfers',
        chain,
        selectCurrentAccountId(global)!,
        enclaveToken,
        chunk,
        resolvedAddress!,
        getNftTransferComment(chain, comment),
        realNativeFee && bigintDivideToNumber(realNativeFee, nfts.length / chunk.length),
        isNftBurn,
      );

      if (batchResult && 'activityIds' in batchResult) {
        global = getGlobal();
        global = updateCurrentTransfer(global, {
          sentNftsCount: (global.currentTransfer.sentNftsCount || 0) + chunk.length,
        });
        setGlobal(global);
      }
      // TODO - process all responses from the API
      result = batchResult;

      if (!batchResult || 'error' in batchResult || 'mfaRequestHash' in batchResult) {
        break;
      }
    }
  } else {
    const { tokenAddress, chain } = selectToken(global, tokenSlug);

    const options: ApiSubmitTransferOptions = {
      accountId: currentAccountId,
      enclaveToken,
      toAddress: resolvedAddress!,
      amount: amount!,
      payload: getTransferPayload(chain, binPayload, comment, shouldEncrypt),
      tokenAddress,
      fee: fullNativeFee,
      realFee: realNativeFee,
      isGasless,
      dieselAmount: diesel && getDieselTokenAmount(diesel),
      stateInit,
      isGaslessWithStars,
      noFeeCheck: true,
      gaslessTransaction: diesel?.transaction,
      sponsorshipId: sponsorship?.id,
    };

    result = await callApi('submitTransfer', chain, options);
  }

  if (isErrorTransferResult(result) && getGlobal().currentTransfer.state === TransferState.Submitting) {
    setGlobal(updateCurrentTransfer(getGlobal(), { state: confirmationState }));
  }
  if (!handleTransferResult(result, updateCurrentTransfer)) {
    return;
  }

  const txId = ('activityIds' in result && result.activityIds[0])
    || ('activityId' in result && result.activityId)
    || undefined;
  const isPending = 'isPending' in result && result.isPending;
  const isBroadcasted = 'isBroadcasted' in result && result.isBroadcasted === true;
  const isPreparing = 'isPreparing' in result && result.isPreparing === true;
  const sponsorshipQuoteId = 'sponsorshipQuoteId' in result
    && typeof result.sponsorshipQuoteId === 'string' ? result.sponsorshipQuoteId : undefined;
  setGlobal(updateCurrentTransfer(getGlobal(), {
    state: ('mfaRequestHash' in result) ? TransferState.ConfirmMfa
      : isPending ? TransferState.Pending : TransferState.Complete,
    txId,
    isBroadcasted,
    isPreparing,
    mfaRequestHash: ('mfaRequestHash' in result && result.mfaRequestHash) || undefined,
  }));

  if (isPending && sponsorshipQuoteId && txId) {
    void (async () => {
      for (let attempt = 0; attempt < 56; attempt++) {
        await pause(attempt < 12 ? 5000 : 15000);
        try {
          const status = await callApi('getWalletSponsoredTransferStatus', currentAccountId, sponsorshipQuoteId);
          const current = getGlobal();
          const isCurrentPendingTransfer = current.currentTransfer.state === TransferState.Pending
            && current.currentTransfer.txId === txId
            && selectCurrentAccountId(current) === currentAccountId;
          if (status?.broadcasted && isCurrentPendingTransfer && !current.currentTransfer.isBroadcasted) {
            setGlobal(updateCurrentTransfer(current, { isBroadcasted: true, isPreparing: false }));
          }
          if (status?.state === 'completed') {
            const activity = selectAccountState(current, currentAccountId)?.activities?.byId?.[txId];
            let next = current;
            if (activity && (activity.status === 'pending' || activity.status === 'pendingTrusted')) {
              next = updateActivity(next, currentAccountId, { ...activity, status: 'completed' });
            }
            if (isCurrentPendingTransfer) {
              next = updateCurrentTransfer(next, { state: TransferState.Complete });
            }
            if (next !== current) setGlobal(next);
            return;
          }
          if (status?.state === 'failed') {
            let next = current;
            if (status.failure_reason === 'not_broadcast') {
              next = removeActivities(next, currentAccountId, [txId]);
            } else if (status.failure_reason === 'chain_failed') {
              const activity = selectAccountState(next, currentAccountId)?.activities?.byId?.[txId];
              if (activity) {
                next = updateActivity(next, currentAccountId, { ...activity, status: 'failed' });
              }
            }
            if (isCurrentPendingTransfer) {
              next = updateCurrentTransfer(next, {
                state: TransferState.Initial,
                sponsorship: undefined,
              });
            }
            if (next !== current) setGlobal(next);
            if (isCurrentPendingTransfer) {
              actions.showError({ error: ApiTransactionDraftError.WalletSponsorshipFailed });
            }
            return;
          }
        } catch {
          // An unavailable status check must never turn an unknown transfer into a failure.
        }
      }
    })();
  }

  actions.fetchTransferDieselState({ tokenSlug });
}));

addActionHandler('cancelTransfer', (global, actions, { shouldReset } = {}) => {
  const { promiseId, tokenSlug, hasFeeBalanceAuthorizationSession } = global.currentTransfer;
  if (hasFeeBalanceAuthorizationSession) {
    const enclaveToken = selectEnclaveToken(global);
    if (enclaveToken) actions.releaseEnclaveSession({ enclaveToken });
  }

  if (shouldReset) {
    if (promiseId) {
      void callApi('cancelDappRequest', promiseId, 'Canceled by the user');
    }

    global = clearCurrentTransfer(global);
    global = updateCurrentTransfer(global, { tokenSlug });

    setGlobal(global);
    return;
  }

  if (getDoesUsePinPad()) {
    global = clearIsPinAccepted(global);
  }
  global = updateCurrentTransfer(global, { state: TransferState.None });
  setGlobal(global);
});

addActionHandler('fetchTransferDieselState', async (global, actions, { tokenSlug }) => {
  const { tokenAddress, chain } = selectToken(global, tokenSlug);
  if (!tokenAddress || chain !== 'ton') return;

  const diesel = await callApi('fetchEstimateDiesel', selectCurrentAccountId(global)!, chain, tokenAddress);
  if (!diesel) return;

  global = getGlobal();
  if (hasCurrentTokenChanged(global, tokenSlug)) {
    return;
  }

  const currentAccountId = selectCurrentAccountId(global)!;
  const accountState = selectAccountState(global, currentAccountId);
  global = preserveMaxTransferAmount(global, updateCurrentTransfer(global, { diesel }));
  if (accountState?.isDieselAuthorizationStarted && diesel.status !== 'not-authorized') {
    global = updateAccountState(global, currentAccountId, { isDieselAuthorizationStarted: undefined });
  }
  setGlobal(global);
});

addActionHandler('checkTransferAddress', async (global, actions, { address, chain }) => {
  if (!address || !chain) {
    global = updateCurrentTransfer(global, { toAddressName: undefined, resolvedAddress: undefined });
    setGlobal(global);

    return;
  }

  const network = selectCurrentNetwork(global);
  const result = await callApi('getAddressInfo', chain, network, address);

  global = getGlobal();
  if (isErrorTransferResult(result)) {
    global = updateCurrentTransfer(global, { toAddressName: undefined, resolvedAddress: undefined });
  } else {
    global = updateCurrentTransfer(global, {
      toAddressName: result.addressName,
      resolvedAddress: result.resolvedAddress,
    });
  }
  setGlobal(global);
});

addActionHandler('updateMfaRequestStatus', async (global) => {
  const hash = global.currentTransfer.mfaRequestHash;
  if (!hash) return;

  const result = await callApi('fetchMfaRequest', hash);

  if (result?.isConfirmed) {
    global = getGlobal();
    global = updateCurrentTransfer(global, { state: TransferState.Complete });
    setGlobal(global);
  }
});

function getTransferPayload(
  chain: ApiChain,
  binPayload?: string,
  comment?: string,
  shouldEncryptComment?: boolean,
): ApiTransferPayload | undefined {
  if (!getChainConfig(chain).isTransferPayloadSupported) {
    return undefined;
  }

  if (binPayload) return { type: 'base64', data: binPayload };
  if (comment) return { type: 'comment', text: comment, shouldEncrypt: shouldEncryptComment };
  return undefined;
}

function getNftTransferComment(chain: ApiChain, comment?: string) {
  return getChainConfig(chain).isTransferPayloadSupported ? comment : undefined;
}

/** For cases when the user switches the token before the API result arrives */
function hasCurrentTokenChanged(currentGlobal: GlobalState, oldSlug: string) {
  const { tokenSlug, nfts } = currentGlobal.currentTransfer;
  return oldSlug !== tokenSlug || nfts?.length;
}
